
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Percent, Calendar } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { AffiliateMarketingAssetsSection } from './AffiliateMarketingAssetsSection';
import type { AffiliateMarketingAsset } from '../types';
import { Form } from "@/components/ui/form";

interface AffiliateSettingsSectionProps {
  form: UseFormReturn<any>;
}

export const AffiliateSettingsSection: React.FC<AffiliateSettingsSectionProps> = ({ form }) => {
  const watchedAssets = form.watch("affiliateSettings.marketingAssets") || [];
  
  const handleAssetsChange = (assets: AffiliateMarketingAsset[]) => {
    form.setValue("affiliateSettings.marketingAssets", assets, { 
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Affiliate Program Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Form {...form}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="affiliateSettings.commissionType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Commission Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select commission type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage of Sale</SelectItem>
                      <SelectItem value="fixed">Fixed Amount per Sale</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    How affiliate commissions will be calculated
                  </FormDescription>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="affiliateSettings.commissionRate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Commission Rate</FormLabel>
                  <FormControl>
                    <div className="relative">
                      {form.watch("affiliateSettings.commissionType") === "percentage" ? (
                        <Percent className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      ) : (
                        <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      )}
                      <Input
                        placeholder={form.watch("affiliateSettings.commissionType") === "percentage" ? "10" : "25"}
                        className="pl-10"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    {form.watch("affiliateSettings.commissionType") === "percentage" 
                      ? "Percentage of sale value (e.g., 10% = 0.10)"
                      : "Fixed amount per sale ($)"}
                  </FormDescription>
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="affiliateSettings.minimumPayout"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Payout Amount ($)</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <Input
                        placeholder="50"
                        className="pl-10"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Minimum amount required before affiliate can request payment
                  </FormDescription>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="affiliateSettings.payoutFrequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payout Frequency</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payout frequency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    How often affiliate payments are processed
                  </FormDescription>
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="affiliateSettings.payoutMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payout Method</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payout method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="ach">ACH Direct Deposit</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    How affiliates will receive payments
                  </FormDescription>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="affiliateSettings.autoEmailSummary"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Automated Monthly Summaries
                    </FormLabel>
                    <FormDescription>
                      Send automated performance emails to affiliates each month
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          
          <FormField
            control={form.control}
            name="affiliateSettings.trackingParameters"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tracking Parameters</FormLabel>
                <FormControl>
                  <Input
                    placeholder="ref, aff, source"
                    value={Array.isArray(field.value) ? field.value.join(', ') : field.value}
                    onChange={(e) => field.onChange(e.target.value.split(',').map(param => param.trim()))}
                  />
                </FormControl>
                <FormDescription>
                  URL parameters used to track affiliate referrals (comma separated)
                </FormDescription>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="affiliateSettings.salesTracking"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Sales Tracking
                  </FormLabel>
                  <FormDescription>
                    Track individual sales and calculate commissions automatically
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Form>
        
        <div className="border-t pt-6">
          <AffiliateMarketingAssetsSection 
            assets={watchedAssets}
            onAssetsChange={handleAssetsChange}
          />
        </div>
      </CardContent>
    </Card>
  );
};
